import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import HomeIcon from '@material-ui/icons/Home';
import {
  ButtonBase,
  ListItemIcon,
  Menu,
  MenuItem,
  ListItem,
  ListItemText,
  Collapse,
  List,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { userActions } from '../../store/actions';
import ProfileIcon from './ProfileIcon';
import { useSelector } from 'react-redux';
import { userDataSelector } from '../../store/selectors';
import { ticketIsLoadingSelector as mainPageIsLoadingSelector } from '../../store/selectors/ticket.selector';
import { createSelector } from '@reduxjs/toolkit';
import { webSocketActions } from '../../store/actions/websocket.actions';
import { useWebSocketSelector } from '../../store/selectors/websocket.selector';
import { Link } from 'react-router-dom';
import {
  ExitToAppRounded,
  HomeRounded,
  PieChartRounded,
  TimelineRounded,
  ExpandLess,
  ExpandMore,
} from '@material-ui/icons';
import envCheckers from 'utils/envCheckers';

const dashboardInfo = {
  title: 'Dashboard',
  board: [
    { title: 'Productivity', url: 'productivity' },
    { title: 'Realtime tracking', url: 'realtime-tracking' },
  ],
};
const AppBar = ({ pageName }) => {
  const user = useSelector(userDataSelector);
  const [dashboardIsShow, setDashboardIsShow] = useState(true);
  const shouldShowAvatarSelector = useMemo(
    () =>
      createSelector(
        mainPageIsLoadingSelector,
        userDataSelector,
        (mainIsLoading, user) => !mainIsLoading && !!user,
      ),
    [],
  );

  const shouldShowAvatar = useSelector(shouldShowAvatarSelector);
  const useWebSocket = useSelector(useWebSocketSelector);

  const dispatch = useDispatch();
  const [showMenu, setShowMenu] = useState(null);
  const handleSetShowMenu = (e) => {
    setShowMenu(e.currentTarget);
  };
  const handleClose = () => {
    setShowMenu(null);
  };

  /****************** start: check for holding ctrl or cmd (TODO: find proper place to put this piece of code) ********************/
  const [isHoldingCtrl, setIsHoldingCtrl] = useState(false);

  const _handleKeydown = useRef((e) => {
    if (e.metaKey) {
      // check if holding ctrls
      setIsHoldingCtrl(true);
    }
  });

  const _handleKeyUp = useRef((e) => {
    setIsHoldingCtrl(false);
  });

  useEffect(() => {
    const handlerKeydown = _handleKeydown.current;
    const handlerKeyup = _handleKeyUp.current;
    document.addEventListener('keydown', handlerKeydown);
    document.addEventListener('keyup', handlerKeyup);
    return () => {
      document.removeEventListener('keydown', handlerKeydown);
      document.removeEventListener('keyup', handlerKeyup);
    };
  }, []);

  useEffect(() => {
    console.debug(`AppBar isHoldingCtrl: `, isHoldingCtrl);
  }, [isHoldingCtrl]);
  /****************** end: check for holding ctrl or cmd  ********************/

  const handleToggleWs = () => {
    const newValue = !useWebSocket;
    dispatch(
      webSocketActions.setUseWebsocketToLocalStorage(newValue),
    );
    handleClose();
  };

  return (
    <SAppBarRoot>
      <SLeftBox>
        <SLink to={'/workdesk'}>
          <HomeIcon />
        </SLink>
        <SBold>OCPS</SBold>
        <SText>Web Agent II</SText>
        {pageName && (
          <SPageName>
            <SLine />
            {`${pageName}`}
          </SPageName>
        )}
      </SLeftBox>
      {shouldShowAvatar && (
        <SRightBox>
          <SName>{user.name}</SName>
          <ButtonBase
            onClick={(e) =>
              // dispatch(alertsActions.error('nothing is here yet'))
              handleSetShowMenu(e)
            }
          >
            <ProfileIcon />
          </ButtonBase>
          <Menu
            open={Boolean(showMenu)}
            anchorEl={showMenu}
            onClose={handleClose}
          >
            <MenuItem to="/workdesk" component={Link}>
              <SListItemIcon>
                <HomeRounded />
              </SListItemIcon>
              Workdesk
            </MenuItem>
            {/* <MenuItem to="/dashboard" component={Link}>
              <SListItemIcon>
                <PieChartRounded />
              </SListItemIcon>
              Dashboard
            </MenuItem> */}
            {/* ------------------------------------------ */}
            <div>
              <ListItem
                button
                onClick={() => setDashboardIsShow(!dashboardIsShow)}
              >
                <SListItemIcon>
                  <PieChartRounded />
                </SListItemIcon>
                <ListItemText primary={dashboardInfo.title} />
                {dashboardIsShow ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse
                component="li"
                in={dashboardIsShow}
                timeout="auto"
                unmountOnExit
              >
                <List disablePadding>
                  {dashboardInfo.board.map((e) => {
                    return (
                      <ListItem
                        button
                        to={`/dashboard/${e.url}`}
                        component={Link}
                      >
                        <SListItemText primary={e.title} />
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </div>
            {/* ---------------------------------------------------------- */}
            {/* allow to turn web socket off only for local or dev */}
            {envCheckers.isLocalOrDev() && (
              <MenuItem onClick={() => handleToggleWs()}>
                <SListItemIcon>
                  <TimelineRounded />
                </SListItemIcon>
                {`Turn ${
                  useWebSocket ? 'off' : 'on'
                } realtime update`}
              </MenuItem>
            )}
            <MenuItem
              // if intentionally logout -> clear local storage (user), and localStorage (viewingTicketId)
              onClick={() => dispatch(userActions.logout(true, true))}
            >
              <SListItemIcon>
                <ExitToAppRounded />
              </SListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </SRightBox>
      )}
    </SAppBarRoot>
  );
};

export default AppBar;

const SListItemText = styled(ListItemText)`
  margin-left: 45px;
`;

const SLine = styled.div`
  border: 0.5px solid rgba(255, 255, 255, 0.6);
  height: 24px;
  margin-right: 20px;
`;
const SAppBarRoot = styled.div`
  background-color: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.palette.primary.contrastText};
  top: 0;
  left: 0;
  right: 0;
  // padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  padding: 0 1rem;
  height: 4rem;
`;
const SLeftBox = styled.div`
  display: flex;
  align-items: center;
  & * + * {
    margin-left: 1rem;
  }
`;
const SRightBox = styled.div`
  display: flex;
  align-items: center;
`;
const SBold = styled.div`
  font-weight: bold;
  font-size: 24px;
`;
const SText = styled.div``;
const SName = styled.div`
  margin-right: 1.5rem;
  font-weight: 500;
`;
const SPageName = styled.div`
  color: ${(props) => props.theme.palette.common.white};
  font-weight: 500;
  font-size: ${(props) => props.theme.typography.fontSize.regular};
  line-height: 37px;
  display: flex;
  align-items: center;
`;
// use Link not <a /> to use the react-router-dom
const SLink = styled(Link)`
  text-decoration: inherit;
  color: inherit;
  &:visited {
    text-decoration: inherit;
    color: inherit;
  }
`;
const SListItemIcon = styled(ListItemIcon)`
  &.MuiListItemIcon-root {
    min-width: 35px;
  }
`;
